/*
  CopyProgramToClientDialog — pick a target client and clone an
  exercise program onto their record. Backend endpoint accepts an
  optional `target_client_email` so the same call handles both
  same-client duplicates and cross-client copies.

  The clone always lands as a Draft on the target client so the
  practitioner has a chance to review any safety flags / precautions
  before it goes live — we don't want prescriptions from Client A
  silently activating on Client B.
*/
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, X, User } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function CopyProgramToClientDialog({
  open, onOpenChange, sourceProgram, onCopied,
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [copying, setCopying] = useState(false);
  const [picked, setPicked] = useState(null);
  const qRef = useRef("");
  const seq = useRef(0);

  // Reset dialog state whenever it re-opens for a different program.
  useEffect(() => {
    if (open) {
      setQ(""); setResults([]); setPicked(null); qRef.current = "";
    }
  }, [open, sourceProgram?.id]);

  // Debounced search — 250ms after the last keystroke.
  useEffect(() => {
    qRef.current = q;
    const needle = q.trim();
    if (needle.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      const my = ++seq.current;
      setSearching(true);
      try {
        const r = await api.post("/exercise-programs/bulk/search-clients", { q: needle });
        if (my !== seq.current) return; // stale response
        setResults(r.data?.results || []);
      } catch (e) {
        if (my === seq.current) {
          toast.error("Client search failed", { description: e?.response?.data?.detail || e.message });
        }
      } finally {
        if (my === seq.current) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  async function submit() {
    if (!picked?.client_email || !sourceProgram?.id) return;
    const sourceEmail = (sourceProgram.client_email || "").toLowerCase();
    const targetEmail = (picked.client_email || "").toLowerCase();
    if (sourceEmail === targetEmail) {
      toast.error("Pick a different client — this program is already on this record.");
      return;
    }
    setCopying(true);
    try {
      const r = await api.post(
        `/exercise-programs/${sourceProgram.id}/duplicate`,
        { target_client_email: picked.client_email },
      );
      toast.success(`Copied to ${picked.client_name || picked.client_email}`, {
        description: "Landed as a Draft — review safety flags before activating.",
      });
      onOpenChange(false);
      if (onCopied) onCopied(r.data);
    } catch (e) {
      toast.error("Copy failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setCopying(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="copy-to-client-dialog">
        <DialogHeader>
          <DialogTitle>Copy program to another client</DialogTitle>
          <DialogDescription>
            Copying <b>{sourceProgram?.title || "program"}</b>. The clone
            lands as a Draft on the target client so you can review and
            activate it there.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by client name…"
              className="h-9 pl-7 pr-7 text-sm"
              data-testid="copy-to-client-search"
            />
            {q && (
              <button
                type="button"
                onClick={() => { setQ(""); setResults([]); setPicked(null); }}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-zinc-100"
                aria-label="Clear"
                data-testid="copy-to-client-clear"
              >
                <X className="h-3.5 w-3.5 text-zinc-500" />
              </button>
            )}
          </div>

          {searching && (
            <div className="text-xs text-zinc-500 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Searching…
            </div>
          )}

          {!searching && q.trim().length >= 2 && results.length === 0 && (
            <div className="text-xs text-zinc-500 py-4 text-center"
                 data-testid="copy-to-client-empty">
              No clients match “<b>{q.trim()}</b>”.
            </div>
          )}

          {results.length > 0 && (
            <div className="max-h-72 overflow-y-auto border rounded divide-y divide-zinc-100"
                 data-testid="copy-to-client-results">
              {results.map((r) => {
                const isSource = (r.client_email || "").toLowerCase()
                  === (sourceProgram?.client_email || "").toLowerCase();
                const isPicked = picked?.client_email === r.client_email;
                return (
                  <button
                    type="button"
                    key={r.client_email}
                    onClick={() => !isSource && setPicked(r)}
                    disabled={isSource}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 ${
                      isPicked ? "bg-indigo-50" : "hover:bg-zinc-50"
                    } ${isSource ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid={`copy-to-client-result-${r.client_email}`}
                  >
                    <User className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {(r.client_name || "").trim()
                          || <span className="italic font-normal text-zinc-500">No name on record</span>}
                      </div>
                      <div className="text-2xs text-zinc-500 truncate">
                        {r.client_email}
                        {isSource && (
                          <span className="ml-1 text-amber-700">(current client — pick a different one)</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  disabled={copying}
                  data-testid="copy-to-client-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!picked || copying}
                  data-testid="copy-to-client-submit">
            {copying
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Copying…</>
              : <>Copy to {(picked?.client_name || "").trim() || picked?.client_email || "client"}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
